import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { ThemeContext } from 'styled-components';
import { IconName, IconSize } from '../../../components/Atoms/Icon/Icon.enum';
import useDebounce from '../../../shared/hooks/useDebounce';
import sendGTMEvent from '../../common/analytics/sendGTMEvent';
import { ACCESSORIES_PATH, MIN_CHARACTERS, SEARCH_RESULTS_PATH, } from '../../common/constants/search';
import { Icon } from '../Header/iconsHelper';
import { StyledCancelButton, StyledCancelIconWrapper, StyledDropdownWrapper, StyledErrorText, StyledForm, StyledInputContainer, StyledInputText, StyledReactTypingEffect, StyledWrapper, } from './Autocomplete.styled';
import { MostPopularSearches } from './components/MostPopularSearches/MostPopularSearches';
import { MostPopularSearchesBoxesConr2659 } from './components/MostPopularSearches/MostPopularSearchesBoxesConr2659';
import { RecentlySearched } from './components/RecentlySearched/RecentlySearched';
import { Suggestions } from './components/Suggestions/Suggestions';
import { ERROR_MESSAGE, EXCLUDED_CHARS, PLACEHOLDER_TEXT } from './constants';
import { getRecentSearches, prepareUpdatedRecentSearches, saveRecentSearchesLocally, } from './utils';
export const Autocomplete = forwardRef(({ setSearchPhrase, setSearchBarFocused, isSimpleAutocomplete, noClearButton, isSearchBarVisible, isAutocompleteOpen: isOpen, setIsAutocompleteOpen: setIsOpen, suggestions, isAutocompleteError, isMobileOrTablet, phraseInitialState, mostPopularSearches, onSearchPhraseSelected, onAutocompleHintsClick, abTest, }, ref) => {
    var _a;
    const [recentlySearched, setRecentlySearched] = useState([]);
    const [phrase, setPhrase] = useState(phraseInitialState);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const [inputFocused, setInputFocused] = useState(false);
    const [error, setError] = useState(isAutocompleteError);
    const theme = useContext(ThemeContext);
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const formRef = useRef(null);
    const currentSearchTerm = useDebounce(phrase, 500);
    const showTypingPlaceholder = !inputFocused && !phrase && !isSimpleAutocomplete;
    const showMostPopularAndRecentSearches = !isSimpleAutocomplete && !(phrase && phrase.length > MIN_CHARACTERS);
    useEffect(() => {
        setRecentlySearched(getRecentSearches());
    }, []);
    const pathname = typeof window !== 'undefined' ? window.location.pathname : null;
    useEffect(() => {
        if (pathname) {
            const isSearchResultPage = pathname.includes(SEARCH_RESULTS_PATH);
            if (isSearchResultPage) {
                setPhrase(pathname.split('/')[2].replace('-', ' '));
            }
        }
    }, [pathname]);
    const getSearchRedirectUrl = ({ searchPhrase, params }) => {
        const searchPhraseQuery = searchPhrase === null || searchPhrase === void 0 ? void 0 : searchPhrase.trimStart().replace(/\s*$/g, '').replace(/ /g, '-').toLowerCase();
        const isAccessorySearch = (params === null || params === void 0 ? void 0 : params.category) === 'accessories';
        const queryParams = params && !isAccessorySearch ? `?${new URLSearchParams(params).toString()}` : '';
        return isAccessorySearch
            ? ACCESSORIES_PATH
            : `${SEARCH_RESULTS_PATH}/${searchPhraseQuery}${queryParams}`;
    };
    const searchableElementHandler = useCallback(({ phrase: searchPhrase, params }) => {
        const redirectUrl = getSearchRedirectUrl({ searchPhrase, params });
        if (!isSimpleAutocomplete && searchPhrase) {
            const newSearch = { phrase: searchPhrase, categories: [(params === null || params === void 0 ? void 0 : params.category) || ''] };
            const newRecentSearches = prepareUpdatedRecentSearches(newSearch, recentlySearched);
            saveRecentSearchesLocally(newRecentSearches);
            setRecentlySearched(newRecentSearches);
        }
        onSearchPhraseSelected(redirectUrl);
        searchInputRef.current && searchInputRef.current.blur();
        searchPhrase && setPhrase(searchPhrase);
        setIsOpen(false);
    }, [isSimpleAutocomplete, setIsOpen, onSearchPhraseSelected]);
    const resetForm = useCallback(() => {
        setPhrase('');
        setActiveSuggestionIndex(-1);
        !isSimpleAutocomplete && setInputFocused(false);
    }, [isSimpleAutocomplete]);
    const handleClickOnSearchInput = () => {
        setActiveSuggestionIndex(-1);
        sendGTMEvent({
            event: 'search_intent',
            search_history: recentlySearched.length ? 'yes' : 'no',
        });
        if (!isOpen) {
            setIsOpen(true);
            return;
        }
        !isSimpleAutocomplete && setInputFocused(true);
        setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 500);
    };
    const handleChangeSearchInput = (e) => {
        !isOpen && setIsOpen(true);
        setPhrase(e.target.value.replace(EXCLUDED_CHARS, ' '));
    };
    const handleEnterKey = (event) => {
        event.preventDefault();
        const { keyCode } = event;
        const isEnterKey = keyCode === 13;
        if (phrase && isEnterKey && activeSuggestionIndex === -1) {
            searchableElementHandler({ phrase });
        }
    };
    useEffect(() => {
        if (isMobileOrTablet) {
            if (formRef.current) {
                if (isOpen) {
                    disableBodyScroll(formRef.current);
                    return;
                }
                enableBodyScroll(formRef.current);
            }
        }
        setSearchBarFocused && setSearchBarFocused(isOpen);
    }, [isOpen, phrase, setSearchBarFocused, isMobileOrTablet, isSimpleAutocomplete]);
    useEffect(() => () => {
        isMobileOrTablet && clearAllBodyScrollLocks();
    }, [isMobileOrTablet]);
    useEffect(() => {
        var _a;
        if (!((_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.value) && error)
            setError(false);
    }, [(_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.value]);
    useEffect(() => {
        if (currentSearchTerm)
            setSearchPhrase(currentSearchTerm);
    }, [currentSearchTerm, setSearchPhrase]);
    useImperativeHandle(ref, () => {
        return {
            focus() {
                setTimeout(() => {
                    var _a;
                    (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    setInputFocused(true);
                }, 500);
            },
            contains(node) {
                var _a;
                return ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(node)) || false;
            },
            getSearchRef() {
                return containerRef.current;
            },
        };
    }, []);
    const onHintClick = (params) => {
        onAutocompleHintsClick(params);
        searchableElementHandler(params);
    };
    const showMostPopularSearchesBoxes = abTest && abTest.id === 'conr2659' && abTest.variant === 'v1';
    return (_jsx(StyledWrapper, Object.assign({ ref: containerRef, isDropdownOpen: isOpen }, { children: _jsxs(StyledForm, Object.assign({ ref: formRef, onClick: (e) => e.preventDefault(), onMouseLeave: () => setActiveSuggestionIndex(-1), onSubmit: (e) => {
                e.preventDefault();
                sendGTMEvent({
                    event: 'search_perform',
                    search_term: phrase && phrase.toLowerCase(),
                    search_input: phrase && phrase.toLowerCase(),
                    search_method: 'all_results|enter',
                    search_category: '',
                });
                searchableElementHandler({ phrase });
            } }, { children: [_jsxs(StyledInputContainer, Object.assign({ "data-testid": "header-search", isSearchBarVisible: isSearchBarVisible }, { children: [_jsx(StyledInputText, { ref: searchInputRef, "aria-label": "Search", autoComplete: "off", icon: _jsx(Icon, { color: theme.colorsV2.grayscale.black, name: IconName.DISPLATE_SEARCH_INPUT, stroke: theme.colorsV2.grayscale.black, strokeWidth: 0.5 }, void 0), iconPosition: "left", onChange: handleChangeSearchInput, onClick: handleClickOnSearchInput, onFocus: () => {
                                setInputFocused(true);
                            }, onKeyUp: handleEnterKey, placeholder: showTypingPlaceholder ? '' : PLACEHOLDER_TEXT, placeholderColor: theme.colorsV2.grayscale.black, spellCheck: false, type: "search", value: (phrase === null || phrase === void 0 ? void 0 : phrase.replace(EXCLUDED_CHARS, ' ')) || '' }, void 0), showTypingPlaceholder && (_jsx(StyledReactTypingEffect, { eraseDelay: 2000, eraseSpeed: 200, speed: 200, staticText: "Search for", text: ['designs', 'artworks', 'brands', 'Displates', 'metal posters'], typingDelay: 0 }, void 0)), isMobileOrTablet && isOpen && (_jsx(StyledCancelButton, Object.assign({ color: theme.colorsV2.font.textPrimary, onClick: () => {
                                resetForm();
                                setIsOpen(false);
                            }, role: "link", weight: "700" }, { children: "Cancel" }), void 0))] }), void 0), phrase && !noClearButton && (_jsx(StyledCancelIconWrapper, Object.assign({ isAutocompleteOpen: isOpen, isSearchBarVisible: isSearchBarVisible, onClick: () => {
                        resetForm();
                        !isMobileOrTablet && setIsOpen(false);
                    }, role: "link" }, { children: _jsx(Icon, { name: IconName.CLOSE, size: IconSize.SIZE16, stroke: theme.colorsV2.grayscale.black, strokeWidth: 2 }, void 0) }), void 0)), isOpen && (_jsxs(StyledDropdownWrapper, Object.assign({ "data-testid": "suggestions" }, { children: [_jsx(Suggestions, { activeSuggestionIndex: activeSuggestionIndex, handleClick: onHintClick, isMobileOrTablet: isMobileOrTablet, phrase: phrase, setActiveSuggestionIndex: setActiveSuggestionIndex, setIsOpen: setIsOpen, setPhrase: setPhrase, suggestions: suggestions }, void 0), showMostPopularAndRecentSearches && (_jsxs(_Fragment, { children: [showMostPopularSearchesBoxes ? (_jsx(MostPopularSearchesBoxesConr2659, { handleClick: onHintClick, hasRecentlySearchItems: recentlySearched.length > 0, phrase: phrase }, void 0)) : null, _jsx(RecentlySearched, { handleClick: onHintClick, phrase: phrase, recentlySearched: recentlySearched, setIsOpen: setIsOpen, setPhrase: setPhrase, setRecentlySearched: setRecentlySearched }, void 0), !showMostPopularSearchesBoxes ? (_jsx(MostPopularSearches, { handleClick: onHintClick, hasRecentlySearchItems: recentlySearched.length > 0, mostPopularSearches: mostPopularSearches, phrase: phrase }, void 0)) : null] }, void 0)), error && _jsx(StyledErrorText, { children: ERROR_MESSAGE }, void 0)] }), void 0))] }), void 0) }), void 0));
});
//# sourceMappingURL=Autocomplete.js.map